import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, Mail, Copy, Check } from 'lucide-react';

export const AuthErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);

  const error = searchParams.get('error') || 'unknown_error';
  const errorDescription = searchParams.get('error_description') || 'An error occurred during authentication.';
  const tenant = searchParams.get('tenant') || '';

  const isUserNotAssigned = errorDescription.trim() === 'User is not assigned to the client application';

  const displayMessage = isUserNotAssigned
    ? "You don't have access to this application, request your admin to give access"
    : errorDescription;

  const handleCopy = () => {
    navigator.clipboard.writeText(displayMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="portal-root min-h-screen flex flex-col">
      {/* Top Nav matching Portal layout */}
      <nav className="portal-nav">
        <div className="portal-nav-inner">
          <div className="portal-nav-brand">
            <img src="/brand/light-logo-with-text.svg" alt="Argus" className="h-7 w-auto dark:hidden" />
            <img src="/brand/dark-logo-with-text.svg" alt="Argus" className="h-7 w-auto hidden dark:block" />
            {tenant && (
              <span className="portal-nav-tenant">
                <span className="portal-nav-sep">/</span>
                {tenant}
              </span>
            )}
          </div>
        </div>
      </nav>

      {/* Main card matching Portal/Mgmt design language */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="relative z-10 w-full max-w-lg">
          <div className="backdrop-blur-xl bg-white/60 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-8 sm:p-10 shadow-[0_8px_32px_0_rgba(0,0,0,0.04)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.2)]">
            <div className="text-center space-y-6">
              
              {/* Softer, cleaner alert icon container */}
              <div className="flex justify-center">
                <div className="w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 flex items-center justify-center">
                  <ShieldAlert className="w-6 h-6 text-orange-600 dark:text-orange-500" strokeWidth={2} />
                </div>
              </div>

              {/* Softer Typography (matching Portal Hero gradient/colors) */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Authentication Failed
                </h1>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {error.replace(/_/g, ' ')}
                </p>
              </div>

              {/* Clean message display without "Something went wrong:" prepended */}
              <div className="bg-slate-50/80 dark:bg-slate-950/40 border border-slate-150 dark:border-slate-800/60 rounded-xl p-4 flex items-start justify-between gap-3 text-left">
                <p className="text-sm text-slate-700 dark:text-slate-300 font-medium leading-relaxed flex-1 min-w-0 select-all break-words whitespace-pre-line">
                  {displayMessage}
                </p>
                <button
                  onClick={handleCopy}
                  title="Copy error details"
                  className="p-1.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-350 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 rounded-lg transition-colors flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              {/* Actions aligning with typical portal buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <button
                  onClick={() => navigate('/auth/login')}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 dark:bg-orange-600 dark:hover:bg-orange-700 text-white rounded-xl font-medium transition-all duration-200 active:scale-98 shadow-sm text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>

                <a
                  href="mailto:support@nesterra.com?subject=Login Access Request"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-200 border border-slate-250 dark:border-slate-800 rounded-xl font-medium transition-all duration-200 active:scale-98 shadow-sm text-sm"
                >
                  <Mail className="w-4 h-4" />
                  Contact Support
                </a>
              </div>

              <p className="text-xs text-slate-400 dark:text-slate-500 pt-2">
                If you believe you should have access to this application, please contact your administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthErrorPage;
